import { useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import { PercentageInput } from "@krea/shared/design-system/input/percentage-input";
import {
  type ISegmentedToggleOption,
  SegmentedToggle,
} from "@krea/shared/design-system/SegmentedToggle";

import type { IBuildingCreditBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

type MaxFinancingMode = "ltv" | "ltc";

const MAX_FINANCING_OPTIONS: ISegmentedToggleOption<MaxFinancingMode>[] = [
  { value: "ltv", label: "LTV", ariaLabel: "Loan-to-value" },
  { value: "ltc", label: "LTC", ariaLabel: "Loan-to-cost" },
];

interface IMaxFinancingInputProps {
  control: Control<BidPostData<IBuildingCreditBidInput>>;
  bidFormIndex: number;
  disabled?: boolean;
}

// Same input-with-inline-toggle shape as guarantor commitment —
// backend enforces exactly one of maxFinancingLtv/maxFinancingLtc, never both.
export const MaxFinancingInput = ({
  control,
  bidFormIndex,
  disabled = false,
}: IMaxFinancingInputProps) => {
  const {
    field: { onChange: onChangeLtv, value: ltvValue },
  } = useController({
    name: `bids.${bidFormIndex}.maxFinancingLtv`,
    control,
    // The exactly-one rule is an object-level test: re-validate the sibling
    // so its error clears once this field satisfies the rule.
    rules: { deps: [`bids.${bidFormIndex}.maxFinancingLtc`] },
  });

  const {
    field: { onChange: onChangeLtc, value: ltcValue },
  } = useController({
    name: `bids.${bidFormIndex}.maxFinancingLtc`,
    control,
    rules: { deps: [`bids.${bidFormIndex}.maxFinancingLtv`] },
  });

  // LTC is the default for new bids; an existing LTV bid keeps LTV.
  const [mode, setMode] = useState<MaxFinancingMode>(
    ltvValue != null ? "ltv" : "ltc",
  );

  const handleModeChange = (next: MaxFinancingMode) => {
    setMode(next);
    if (next === "ltv") {
      onChangeLtc(null);
    } else {
      onChangeLtv(null);
    }
  };

  const inputValue: number | null =
    mode === "ltc" ? (ltcValue ?? null) : (ltvValue ?? null);

  const handleChange = (n: number | null) => {
    if (mode === "ltc") {
      onChangeLtc(n);
    } else {
      onChangeLtv(n);
    }
  };

  return (
    <PercentageInput
      id={`max-financing-${bidFormIndex}`}
      value={inputValue}
      onChange={handleChange}
      disabled={disabled}
      placeholder="e.g. 70"
      aria-label={
        mode === "ltc"
          ? "Max financing, loan-to-cost (%)"
          : "Max financing, loan-to-value (%)"
      }
      aria-required
      trailing={
        <SegmentedToggle
          aria-label="Max financing type"
          options={MAX_FINANCING_OPTIONS}
          value={mode}
          onValueChange={handleModeChange}
          disabled={disabled}
        />
      }
    />
  );
};
